// skipcq: JS-W1028, JS-0128
import React, { useMemo } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { orderSplatAwardMonthlyChartRows } from '../../utils/splatAwardMonthlyReport';

const CHART_HEIGHT_PX = 280;

function formatMonthlyEarnedTooltipValue(value) {
  return [Number(value).toLocaleString(), 'Splats earned'];
}

function formatMonthlyEarnedTooltipLabel(label) {
  return typeof label === 'string' && label.length > 0 ? label : 'Month';
}

/**
 * Monthly splats earned bar chart (splatAwardReports rollups).
 */
function SplatMonthlyEarnedChart({ rows, loading, error }) {
  const theme = useTheme();

  const chartRowsAscending = useMemo(
    () => orderSplatAwardMonthlyChartRows(rows),
    [rows],
  );

  const tooltipContentStyle = useMemo(
    () => ({
      backgroundColor: theme.palette.background.paper,
      border: `1px solid ${theme.palette.divider}`,
      borderRadius: 8,
      color: theme.palette.text.primary,
    }),
    [theme],
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          py: 2,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error">
        {error}
      </Typography>
    );
  }

  if (chartRowsAscending.length === 0) {
    return (
      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
        No monthly splat reports yet — run the scheduled reporter or backfill script.
      </Typography>
    );
  }

  return (
    <Box
      data-testid="splat-monthly-earned-chart"
      sx={{ width: '100%', height: CHART_HEIGHT_PX }}
    >
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={chartRowsAscending}
          margin={{ top: 8, right: 12, left: 4, bottom: 48 }}
        >
          <XAxis
            dataKey="monthLabel"
            tick={{ fontSize: 11 }}
            angle={-35}
            textAnchor="end"
            height={56}
            interval={0}
          />
          <YAxis tick={{ fontSize: 12 }} allowDecimals={false} width={48} />
          <Tooltip
            contentStyle={tooltipContentStyle}
            formatter={formatMonthlyEarnedTooltipValue}
            labelFormatter={formatMonthlyEarnedTooltipLabel}
          />
          <Bar
            dataKey="totalSplatsEarned"
            fill={theme.palette.primary.main}
            radius={[4, 4, 0, 0]}
            maxBarSize={48}
          />
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
}

export default SplatMonthlyEarnedChart;
